import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { VideoService, type VideoWithCategory } from '../services/videos'
import { AdminSettingsService } from '../services/adminSettings'
import hashtagIcon from '../assets/icons/hashtag-solid.png'
import VideoThumbnail from '../components/VideoThumbnail'
import Layout from '../components/Layout'
import FirstTimeUserDialog from '../components/FirstTimeUserDialog'
import { AuthService } from '../services/auth'
import { useAdminSelectedSchool } from '../utils/adminSchoolContext'
import { supabase } from '../lib/supabase'
import iconRecommend from '../assets/icons/icon-recommend.png'
import iconRanking from '../assets/icons/icon-ranking.png'
import iconStar from '../assets/icons/icon-star.png'
import iconSchool from '../assets/icon/school-icon-mark.svg'
import HorizontalScroller from '../components/HorizontalScroller'
import CurrentHomeworkSection from '../components/CurrentHomeworkSection'
import { HomeworkService, type HomeworkSubmission, type Homework, type HomeworkSelection } from '../services/homework'
import { GroupService } from '../services/groups'
import aiMessageIcon from '../assets/icons/ai-message-solid.png'

interface FeaturedVideoGroup {
  id: string
  name: string
  description: string | null
  display_order: number
  is_active: boolean
  featured_videos: {
    id: string
    video_id: string
    display_order: number
    video: VideoWithCategory
  }[]
}

// Helper function to safely render tags
const renderTags = (tags: unknown, maxTags: number = 3, sizeClass: string = "text-xs", compact: boolean = false) => {
  // Type guard to ensure tags is a proper array of tag objects
  if (!Array.isArray(tags) || tags.length === 0) {
    return <span className={`${sizeClass} text-gray-400`}>タグなし</span>
  }

  // Filter and validate tag objects
  const validTags = tags
    .filter((tag): tag is { name: string; slug: string } =>
      tag &&
      typeof tag === 'object' &&
      'name' in tag &&
      'slug' in tag &&
      typeof tag.name === 'string' &&
      typeof tag.slug === 'string'
    )
    .slice(0, maxTags)

  if (validTags.length === 0) {
    return <span className={`${sizeClass} text-gray-400`}>タグなし</span>
  }

  // Determine icon size and padding based on text size and compact mode
  const isSmall = sizeClass === "text-xs"
  const iconClass = isSmall ? "w-3 h-3" : "w-4 h-4"
  const paddingClass = compact
    ? (isSmall ? "px-1 py-0.5" : "px-1.5 py-0.5")
    : (isSmall ? "px-1 py-0.5" : "px-2 py-1")
  const gapClass = compact ? "gap-0.5" : "gap-1"

  return validTags.map(tag => (
    <Link
      key={tag.slug}
      to={`/tags/${encodeURIComponent(tag.slug)}`}
      className={`flex items-center ${gapClass} ${sizeClass} hover:bg-gray-100 rounded ${paddingClass} transition-colors`}
      onClick={(e) => e.stopPropagation()}
    >
      <img src={hashtagIcon} alt="#" className={iconClass} />
      <span className="font-semibold text-gray-800">{tag.name}</span>
    </Link>
  ))
}

// Section video card component
const SectionVideoCard = ({ video, showTags = true }: { video: VideoWithCategory; showTags?: boolean }) => {
  const navigate = useNavigate()

  return (
    <div className="block group flex-shrink-0 w-56">
      <div className="bg-white rounded-lg overflow-hidden hover:shadow-md transition-shadow">
        {/* Clickable area for thumbnail and title */}
        <div
          className="cursor-pointer"
          onClick={() => navigate(`/video/${video.id}`)}
        >
          <div className="relative">
            <VideoThumbnail
              thumbnailUrl={video.thumbnail_url}
              title={video.title}
              thumbnailTitle={video.thumbnail_title}
              schoolOriginal={video.is_school_video && video.video_type === 'chat_only'}
              className="w-full h-32"
            />
          </div>
          <div className="px-3 pt-3">
            {/* Always reserve 2 lines (20px each) so every card is the same height */}
            <h3 className={`font-bold text-sm line-clamp-2 min-h-10 text-theme-primary ${showTags ? 'mb-1' : ''}`}>
              {video.title}
            </h3>
          </div>
        </div>

        {/* Tags - separate from clickable area.
            Always reserve exactly 2 rows (20px row + 8px gap = 48px), even when
            there are no tags, so the hover frame is identical on every card. */}
        {showTags && (
          <div className="px-3 pb-3">
            <div className="flex flex-wrap gap-2 h-12 overflow-hidden">
              {!video.is_school_video && renderTags(video.tags, 3, "text-xs")}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}


// Horizontal scrollable section component
const HorizontalVideoSection = ({
  title,
  videos,
  icon = iconRecommend,
  showTags = true
}: {
  title: string
  videos: VideoWithCategory[]
  icon?: string
  // The school's own sections hold mostly untagged school content, so they drop
  // the tag row instead of reserving empty space under every card.
  showTags?: boolean
}) => {
  return (
    <div className="mb-8">
      <div className="bg-transparent flex items-center gap-3 mb-2">
        <img src={icon} alt="" className="w-[45px] h-10 object-contain" />
        <h2 className="font-bold text-xl text-gray-900">{title}</h2>
      </div>
      <div className="bg-white rounded-lg shadow-sm">
        <HorizontalScroller className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth p-4 scroll-px-4">
          {videos.map(video => (
            <div key={video.id}>
              <SectionVideoCard video={video} showTags={showTags} />
            </div>
          ))}
        </HorizontalScroller>
      </div>
    </div>
  )
}

// System-wide featured groups don't depend on who is signed in, so the page starts
// this query alongside the auth check instead of after it.
const fetchSystemFeaturedGroups = async () => await supabase
    .from('featured_video_groups')
    .select(`
      *,
      featured_videos (
        id,
        video_id,
        display_order,
        video:videos (
          id,
          title,
          thumbnail_url,
          thumbnail_title,
          video_type,
          is_school_video,
          video_tags (
            tags (
              id,
              name,
              slug
            )
          )
        )
      )
    `)
    .filter('school_id', 'is', null)
    .eq('is_active', true)
    .order('display_order', { ascending: true })

interface Prefetch {
  featuredGroups: ReturnType<typeof fetchSystemFeaturedGroups>
  rankingPeriodDays: Promise<number>
}

type LoadSection = 'homework' | 'popular' | 'newVideos' | 'featured' | 'schoolGroups'
const NO_SECTIONS_LOADED: Record<LoadSection, boolean> = {
  homework: false, popular: false, newVideos: false, featured: false, schoolGroups: false,
}

const VideosPage = () => {
  const [newVideos, setNewVideos] = useState<VideoWithCategory[]>([])
  const [popularVideos, setPopularVideos] = useState<VideoWithCategory[]>([])
  const [popularTitle, setPopularTitle] = useState('みんなの人気コンテンツ')
  const [featuredGroups, setFeaturedGroups] = useState<FeaturedVideoGroup[]>([])
  const [schoolFeaturedGroups, setSchoolFeaturedGroups] = useState<FeaturedVideoGroup[]>([])
  // Each section renders as soon as its own query finishes, so the page no longer
  // waits on the slowest one; until then a skeleton holds its place.
  const [loadedSections, setLoadedSections] = useState<Record<LoadSection, boolean>>(NO_SECTIONS_LOADED)
  const [error, setError] = useState('')
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [onboardingCompleted, setOnboardingCompleted] = useState(false)
  const [schoolHomeworks, setSchoolHomeworks] = useState<Homework[]>([])
  const [entreHomeworks, setEntreHomeworks] = useState<Homework[]>([])
  const [systemSelection, setSystemSelection] = useState<HomeworkSelection | null>(null)
  const [homeworkSubmissions, setHomeworkSubmissions] = useState<HomeworkSubmission[]>([])
  const [isInEntreClub, setIsInEntreClub] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const adminSelectedSchool = useAdminSelectedSchool()
  const newChatMessageRef = useRef('')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    checkAuthAndLoadData()
  }, [adminSelectedSchool]) // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuthAndLoadData = async () => {
    try {
      // Queries that don't need the signed-in user start now, in parallel with the
      // auth check, rather than waiting for it.
      const prefetch: Prefetch = {
        featuredGroups: fetchSystemFeaturedGroups(),
        rankingPeriodDays: AdminSettingsService.getSettingValue<number>('ranking_period_days', 30).catch(() => 30),
      }

      // Check if user is authenticated
      const user = await AuthService.getCurrentUser()
      if (!user) {
        // Redirect to landing page with login dialog
        navigate('/?login=true')
        return
      }

      const isAdminUser = user.role === 'admin' || user.role === 'super_admin'
      setIsAdmin(isAdminUser)

      const effectiveSchoolId = isAdminUser ? (adminSelectedSchool?.id ?? null) : user.school_id

      // Start loading dashboard data immediately - it does not depend on the
      // onboarding flow status, so we don't await the (extra auth round-trip)
      // onboarding check before kicking off the data fetches.
      const loadDataPromise = loadData(prefetch, user.id, isAdminUser, effectiveSchoolId, user.grade, user.class)

      // Resolve onboarding flow status only when the onboarding flow is
      // actually requested - it triggers extra auth round-trips (isOAuthUser)
      // and its result is unused on a normal navigation.
      const onboardingParam = searchParams.get('onboarding')
      if (onboardingParam === 'true') {
        const flowStatus = await AuthService.getOnboardingFlowStatus(user)
        // Handle onboarding flow - only if not already completed this session
        if (flowStatus.needsOnboarding && !onboardingCompleted) {
          setShowOnboarding(true)
        }
        // Remove parameters from URL either way
        setSearchParams(new URLSearchParams())
      }

      await loadDataPromise
    } catch (error) {
      console.error('Auth check failed:', error)
      navigate('/?login=true')
    }
  }

  const loadData = async (prefetch: Prefetch, userId: string, isAdminUser: boolean, schoolId?: string | null, grade?: string | null, className?: string | null) => {
    const markLoaded = (section: LoadSection) =>
      setLoadedSections(prev => ({ ...prev, [section]: true }))
    try {
      setLoadedSections(NO_SECTIONS_LOADED)

      // The video, featured-group, and homework queries are mutually
      // independent (they only need userId/schoolId/grade/class), so run them
      // concurrently instead of in a serial waterfall. Each task does its own
      // fetch + transform + setState and handles its own errors, so one failing
      // query leaves its section empty rather than rejecting the whole batch.
      const now = new Date().toISOString()

      // Load new videos (first 15 newest published non-demo videos) with database filtering
      // Exclude videos with future published_at dates
      const newVideosTask = (async () => {
        let newVideosQuery = supabase
          .from('videos')
          .select(`
            *,
            categories!category_id (
              id,
              name,
              slug
            ),
            video_tags (
              tags (
                id,
                name,
                slug
              )
            )
          `)

        // Include this school's content alongside system-wide videos; otherwise system-wide only.
        if (schoolId) {
          newVideosQuery = newVideosQuery.or(`school_id.is.null,school_id.eq.${schoolId}`)
        } else {
          newVideosQuery = newVideosQuery.is('school_id', null)
        }

        const { data: newVideosData, error: newVideosError } = await newVideosQuery
          .neq('video_type', 'demo')
          .not('published_at', 'is', null)
          .lte('published_at', now)
          .order('published_at', { ascending: false })
          .limit(15)

        if (newVideosError) {
          console.error('Failed to load new videos:', newVideosError)
        } else {
          // Transform new videos to include tags array with both name and slug
          const transformedNewVideos = (newVideosData || []).map(video => ({
            ...video,
            categories: video.categories || null,
            tags: video.video_tags?.map((vt: any) => ({ name: vt.tags.name, slug: vt.tags.slug })) || []
          }))
          setNewVideos(transformedNewVideos)
        }
      })()

      // Load popular videos. When the school has views in the admin-configured window, show
      // 校内ランキング (ranked by this school's views, so old videos don't stay pinned).
      // Otherwise show みんなの人気コンテンツ, ranked by the all-schools view_count.
      const popularVideosTask = (async () => {
        try {
          if (schoolId) {
            const periodDaysRaw = await prefetch.rankingPeriodDays
            const periodDays = Number(periodDaysRaw) > 0 ? Number(periodDaysRaw) : null
            const { videos: rankedVideos, hasSchoolViews } = await VideoService.getSchoolRankedVideos(schoolId, periodDays, 15)
            if (hasSchoolViews) {
              setPopularTitle('校内ランキング')
              setPopularVideos(rankedVideos)
              return
            }
          }

          setPopularTitle('みんなの人気コンテンツ')

          const { data: popularVideosData, error: popularVideosError } = await supabase
            .from('videos')
            .select(`
              *,
              categories!category_id (
                id,
                name,
                slug
              ),
              video_tags (
                tags (
                  id,
                  name,
                  slug
                )
              )
            `)
            .is('school_id', null)
            .neq('video_type', 'demo')
            .order('view_count', { ascending: false })
            .limit(15)

          if (popularVideosError) {
            console.error('Failed to load popular videos:', popularVideosError)
          } else {
            // Transform popular videos to include tags array with both name and slug
            const transformedPopularVideos = (popularVideosData || []).map(video => ({
              ...video,
              categories: video.categories || null,
              tags: video.video_tags?.map((vt: any) => ({ name: vt.tags.name, slug: vt.tags.slug })) || []
            }))
            setPopularVideos(transformedPopularVideos)
          }
        } catch (popularError) {
          console.error('Failed to load popular videos:', popularError)
        }
      })()

      // Load featured video groups
      const featuredGroupsTask = (async () => {
        const { data: groupsData, error: groupsError } = await prefetch.featuredGroups

        if (groupsError) {
          console.error('Failed to load featured groups:', groupsError)
        } else {
          // Sort featured videos within each group by display_order and transform tags
          const sortedGroups = (groupsData || []).map(group => ({
            ...group,
            featured_videos: group.featured_videos
              .sort((a: { display_order: number }, b: { display_order: number }) => a.display_order - b.display_order)
              .map((fv: any) => ({
                ...fv,
                video: {
                  ...fv.video,
                  tags: fv.video.video_tags?.map((vt: any) => ({ name: vt.tags.name, slug: vt.tags.slug })) || []
                }
              }))
          }))
          setFeaturedGroups(sortedGroups)
        }
      })()

      // Load school-specific featured groups
      const schoolGroupsTask = (async () => {
        if (!schoolId) return
        const { data: schoolGroupsData, error: schoolGroupsError } = await supabase
          .from('featured_video_groups')
          .select(`
            *,
            featured_videos (
              id,
              video_id,
              display_order,
              video:videos (
                id,
                title,
                thumbnail_url,
                thumbnail_title,
                video_type,
                is_school_video,
                video_tags (
                  tags (
                    id,
                    name,
                    slug
                  )
                )
              )
            )
          `)
          .eq('school_id', schoolId)
          .eq('is_active', true)
          .order('display_order', { ascending: true })

        if (!schoolGroupsError) {
          const sortedSchoolGroups = (schoolGroupsData || []).map(group => ({
            ...group,
            featured_videos: group.featured_videos
              .sort((a: { display_order: number }, b: { display_order: number }) => a.display_order - b.display_order)
              .map((fv: any) => ({
                ...fv,
                video: {
                  ...fv.video,
                  tags: fv.video?.video_tags?.map((vt: any) => ({ name: vt.tags.name, slug: vt.tags.slug })) || []
                }
              }))
          }))
          setSchoolFeaturedGroups(sortedSchoolGroups)
        }
      })()

      // Load homework data. Everything is fetched in one parallel round: the active
      // school/entre homework reuse the submissions being loaded here instead of
      // fetching them again, and the entre homework is requested without waiting for the
      // club membership check — it is only shown when the student is in the club.
      const homeworkTask = (async () => {
        try {
          const subsPromise = HomeworkService.getMySubmissions(userId)
          const submittedHomeworkIds = subsPromise.then(subs => new Set(subs.map(s => s.homework_id)))
          submittedHomeworkIds.catch(() => {}) // handled through subsPromise below
          const [subs, selection, studentGroups, active, entre] = await Promise.all([
            subsPromise,
            HomeworkService.getSystemHomeworkSelection(userId),
            isAdminUser ? Promise.resolve([]) : GroupService.getStudentGroups(userId),
            schoolId
              ? HomeworkService.getActiveSchoolHomeworks({ studentId: userId, schoolId, grade: grade ?? null, className: className ?? null, submittedHomeworkIds })
              : Promise.resolve([] as Homework[]),
            schoolId
              ? HomeworkService.getActiveEntreHomeworks({ studentId: userId, schoolId, submittedHomeworkIds })
              : Promise.resolve([] as Homework[]),
          ])
          const inEntreClub = studentGroups.some(g => g.is_entre_club)
          setHomeworkSubmissions(subs)
          setSystemSelection(selection)
          setIsInEntreClub(inEntreClub)
          setSchoolHomeworks(active)
          setEntreHomeworks(inEntreClub || isAdminUser ? entre : [])
        } catch (homeworkError) {
          console.warn('Failed to load homework:', homeworkError)
        }
      })()

      await Promise.all([
        newVideosTask.finally(() => markLoaded('newVideos')),
        popularVideosTask.finally(() => markLoaded('popular')),
        featuredGroupsTask.finally(() => markLoaded('featured')),
        schoolGroupsTask.finally(() => markLoaded('schoolGroups')),
        homeworkTask.finally(() => markLoaded('homework')),
      ])
    } catch (error) {
      console.error('Failed to load videos:', error)
      setError('動画の読み込みに失敗しました')
      setLoadedSections({ homework: true, popular: true, newVideos: true, featured: true, schoolGroups: true })
    }
  }


  // Handle onboarding completion
  const handleOnboardingComplete = async () => {
    setShowOnboarding(false)
    setOnboardingCompleted(true) // Mark onboarding as completed for this session
    // Clean up URL parameters when onboarding is complete
    setSearchParams(new URLSearchParams())

    // Force refresh user data from database to ensure fresh state
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      // Get fresh user data from database
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()

      if (userData) {
        console.log('Onboarding completed successfully - fresh user data:', {
          first_login_completed: userData.first_login_completed,
          password_reset_required: userData.password_reset_required
        })
      }
    }
  }


  const handleNewChatSubmit = () => {
    const msg = newChatMessageRef.current.trim()
    navigate('/chat', { state: msg ? { initialMessage: msg } : undefined })
  }

  return (
    <Layout>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        {/* Error State */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Onboarding State - Clean background during onboarding */}
        {!error && showOnboarding && (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#da4574] mx-auto mb-4"></div>
              <p className="text-gray-600">
                初期設定中...
              </p>
            </div>
          </div>
        )}

        {/* Content */}
        {!error && !showOnboarding && (
          <div className="space-y-8">
            <div className="relative">
{/* 現在の宿題 */}
              <div className="flex flex-col gap-4 mt-4">
                {loadedSections.homework && (
                  <CurrentHomeworkSection
                    schoolHomeworks={schoolHomeworks}
                    entreHomeworks={entreHomeworks}
                    showEntreCard={isInEntreClub || isAdmin}
                    systemSelection={systemSelection}
                    submissions={homeworkSubmissions}
                  />
                )}
              </div>

              {/* 探究・相談しよう! */}
              <div className="flex flex-col gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <div className="relative h-10 w-[45px] shrink-0">
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 45 40" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                      <path fillRule="evenodd" clipRule="evenodd" d="M20 0C8.9541 0 0 8.9541 0 20C0 31.0459 8.9541 40 20 40C28.5059 40 35.7715 34.6904 38.6631 27.2051C38.8794 26.7158 39.1377 26.25 39.4336 25.8105C39.7515 25.3398 40.1128 24.9004 40.5117 24.498L45 20L40.5117 15.5C39.7378 14.7197 39.1055 13.7988 38.6572 12.7793C35.7617 5.30176 28.5 0 20 0Z" fill="#da4574"/>
                    </svg>
                    <div className="absolute left-[10px] top-[10px] w-5 h-5">
                      <img src={aiMessageIcon} alt="" className="absolute inset-0 w-full h-full brightness-0 invert" />
                    </div>
                  </div>
                  <span className="text-[#33333b] text-xl font-bold">探究・相談しよう!</span>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="flex flex-1 rounded-full overflow-hidden border border-[#e6e0e0] shadow-sm bg-white">
                    <input
                      type="text"
                      onChange={e => { newChatMessageRef.current = e.target.value }}
                      placeholder="気になることや相談したいことを入力してね"
                      className="flex-1 px-7 py-3 text-base text-[#72727c] bg-white outline-none"
                    />
                    <button
                      onClick={handleNewChatSubmit}
                      className="px-6 bg-theme-tertiary hover:bg-theme-tertiary/90 text-white text-sm font-medium transition-colors"
                    >
                      送信
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* School-specific Featured Video Groups (shown first) */}
            {schoolFeaturedGroups.map((group) => (
              <HorizontalVideoSection
                key={group.id}
                title={group.name}
                icon={iconSchool}
                showTags={false}
                videos={group.featured_videos.map(fv => fv.video).filter(Boolean)}
              />
            ))}

            {/* Popular Videos Section */}
            {loadedSections.popular && (
              <HorizontalVideoSection
                title={popularTitle}
                videos={popularVideos}
                icon={iconRanking}
              />
            )}

            {/* New Videos Section */}
            {loadedSections.newVideos && (
              <HorizontalVideoSection
                title="新着コンテンツ"
                videos={newVideos}
                icon={iconStar}
              />
            )}

            {/* System-wide Featured Video Groups */}
            {featuredGroups.map((group) => (
              <HorizontalVideoSection
                key={group.id}
                title={group.name}
                videos={group.featured_videos.map(fv => fv.video).filter(Boolean)}
              />
            ))}
          </div>
        )}
      </div>

      {/* First Time User Onboarding Dialog */}
      <FirstTimeUserDialog
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
      />
    </Layout>
  )
}

export default VideosPage